import { getColors } from '../../charts/shared/palettes';
import { JN, LEGEND_SPACING, TITLE_SPACING, fileHeader } from './shared';

export function genRadar(chartType, config, t, data) {
  const colors = getColors(config, Math.max(data.length, 1), t);

  const axes = data.length && data[0].values ? Object.keys(data[0].values) : [];
  const maxPerAxis = {};
  axes.forEach(axis => {
    maxPerAxis[axis] = Math.max(...data.map(d => d.values[axis] || 0), 1);
  });

  const hasTitle = !!(config.title && String(config.title).trim());
  const showLegend = !!(config.showLegend && data.length > 1);
  const showValues = !!config.showValues;
  const showAxisLabels = !!config.showAxisLabels;
  const fillAlpha = config.fillAlpha ?? 0.2;

  return (
    fileHeader(['react'], 'radar') +
    `import React from 'react';

const DATA = ${JN(data)};
const COLORS = ${JN(colors)};
const AXES = ${JN(axes)};
const MAX_PER_AXIS = ${JN(maxPerAxis)};
const THEME = ${JN(t)};
const FILL_ALPHA = ${fillAlpha};
const GRID_LEVELS = [0.2, 0.4, 0.6, 0.8, 1.0];

function polar(cx, cy, radius, angle, t) {
  return { x: cx + t * radius * Math.cos(angle), y: cy + t * radius * Math.sin(angle) };
}

function labelAlignment(angle) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    textAnchor: cos > 0.15 ? 'start' : cos < -0.15 ? 'end' : 'middle',
    dominantBaseline: sin > 0.15 ? 'hanging' : sin < -0.15 ? 'auto' : 'middle',
  };
}

export default function Chart({ width = 720, height = 480 }) {
  const N = AXES.length;
  const titleHeight = ${hasTitle ? 45 : 15};
  const legendHeight = ${showLegend ? 40 : 0};
  const labelMargin = 48;
  const cx = width / 2;
  const availableH = height - titleHeight - legendHeight - labelMargin * 2;
  const availableW = width - labelMargin * 2;
  const radius = Math.min(availableW, availableH) / 2;
  const cy = titleHeight + labelMargin + radius;
  const angleFor = (i) => -Math.PI / 2 + (i / N) * 2 * Math.PI;

  const legendY = cy + radius + labelMargin - 8;
  const legendItemWidth = 90;
  const legendStartX = Math.max(20, (width - DATA.length * legendItemWidth) / 2);

  if (!N) return null;

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>
      ${hasTitle ? `<div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, width: '100%', textAlign: 'center', fontSize: 18, fontWeight: 400, color: THEME.titleColor }}>${String(config.title).replace(/</g, '&lt;')}</div>` : ''}
      <svg width={width} height={height}>
        {GRID_LEVELS.map(level => {
          const points = AXES.map((_, i) => { const pt = polar(cx, cy, radius, angleFor(i), level); return pt.x + ',' + pt.y; }).join(' ');
          return <polygon key={'grid-' + level} points={points} fill="none" stroke={THEME.gridColor} strokeWidth={0.8} opacity={THEME.gridAlpha} />;
        })}
        {AXES.map((_, i) => {
          const tip = polar(cx, cy, radius, angleFor(i), 1);
          return <line key={'spoke-' + i} x1={cx} y1={cy} x2={tip.x} y2={tip.y} stroke={THEME.gridColor} strokeWidth={0.8} opacity={THEME.gridAlpha} />;
        })}
        {DATA.map((series, si) => {
          const color = COLORS[si] || '#4C6EE6';
          const pts = AXES.map((axis, i) => {
            const t = (series.values[axis] || 0) / MAX_PER_AXIS[axis];
            const pt = polar(cx, cy, radius, angleFor(i), t);
            return pt.x + ',' + pt.y;
          }).join(' ');
          return (
            <g key={'series-' + si}>
              <polygon points={pts} fill={color} fillOpacity={FILL_ALPHA} stroke={color} strokeWidth={2} strokeLinejoin="round" />
              {AXES.map((axis, i) => {
                const t = (series.values[axis] || 0) / MAX_PER_AXIS[axis];
                const pt = polar(cx, cy, radius, angleFor(i), t);
                return <circle key={'dot-' + si + '-' + i} cx={pt.x} cy={pt.y} r={3.5} fill={color} />;
              })}
              ${showValues ? `{AXES.map((axis, i) => {
                const angle = angleFor(i);
                const t2 = (series.values[axis] || 0) / MAX_PER_AXIS[axis];
                const pt = polar(cx, cy, radius, angle, t2);
                const lx = pt.x + 14 * Math.cos(angle);
                const ly = pt.y + 14 * Math.sin(angle);
                const { textAnchor } = labelAlignment(angle);
                return <text key={'val-' + si + '-' + i} x={lx} y={ly} textAnchor={textAnchor} dominantBaseline="middle" fontSize={9} fill={color}>{series.values[axis]}</text>;
              })}` : ''}
            </g>
          );
        })}
        ${showAxisLabels ? `{AXES.map((axis, i) => {
          const angle = angleFor(i);
          const pt = polar(cx, cy, radius, angle, 1.18);
          const { textAnchor, dominantBaseline } = labelAlignment(angle);
          return <text key={'lbl-' + i} x={pt.x} y={pt.y} textAnchor={textAnchor} dominantBaseline={dominantBaseline} fontSize={11} fill={THEME.tickColor}>{axis}</text>;
        })}` : ''}
        ${showLegend ? `{DATA.map((series, si) => {
          const color = COLORS[si] || '#4C6EE6';
          const lx = legendStartX + si * legendItemWidth;
          return (
            <g key={'leg-' + si} transform={'translate(' + lx + ', ' + legendY + ')'}>
              <rect x={0} y={-${Math.round(LEGEND_SPACING.rectSize / 2)}} width={${LEGEND_SPACING.rectSize}} height={${LEGEND_SPACING.rectSize}} fill={color} fillOpacity={0.8} />
              <text x={${LEGEND_SPACING.rectTextX}} y={1} dominantBaseline="middle" fontSize={11} fill={THEME.foreground}>{series.name}</text>
            </g>
          );
        })}` : ''}
      </svg>
    </div>
  );
}
`
  );
}
